/**
 * ClaimWithCodeSheet — the "Use a school code" branch of the "Add student"
 * affordance on /my-family.
 *
 * A parent with a school invitation enters its activation code here to
 * bulk-claim every student the school already enrolled under that
 * invitation. This is distinct from the manual `AddStudentSheet`, which
 * creates a single family-managed student — so the two live behind a
 * chooser step (`AddMemberSheet` mode `student-method`) rather than in one
 * form.
 *
 * Uses `FormSheet` so it's a native `<form>` (Enter submits) that auto-
 * closes on a successful claim. A successful-but-pending outcome still
 * closes + refetches; a rejected code keeps the sheet open with an inline
 * error.
 */

import { Button } from '@ordo/ui/button'
import { FormField } from '@ordo/ui/form-field'
import { FormSheet } from '@ordo/ui/form-sheet'
import { Input } from '@ordo/ui/input'
import { toast } from '@ordo/ui/sonner'
import { useState } from 'react'

import { useClaimActivationCodeMutation } from '@/api/family'

import { formatClaimedStudentsMessage } from './helpers'

export interface ClaimWithCodeSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called after a code is accepted (claimed or pending) — use to refetch. */
  onClaimed?: () => void
  /**
   * Called with the claimed students' user ids on a CLAIMED outcome — the
   * caller checks them for missing REQUIRED facts and opens the
   * complete-student-info sheet.
   */
  onClaimedStudents?: (studentUserIds: string[]) => void
  /** When provided, renders a "Back" button that returns to the chooser. */
  onBack?: () => void
}

export function ClaimWithCodeSheet({
  open,
  onOpenChange,
  onClaimed,
  onClaimedStudents,
  onBack,
}: ClaimWithCodeSheetProps) {
  const [activationCode, setActivationCode] = useState('')
  const claimMutation = useClaimActivationCodeMutation()

  async function handleSubmit() {
    const code = activationCode.trim()
    if (!code) {
      throw new Error('Enter an activation code.')
    }
    let result
    try {
      result = await claimMutation.mutateAsync({ activationCode: code })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Activation code was not accepted.'
      toast.error('Code not accepted', { description: message })
      // Rethrow so FormSheet keeps the sheet open + shows the inline error.
      throw new Error(message, { cause: err })
    }
    if (result.outcome === 'CLAIMED') {
      toast.success('Students claimed', {
        description: formatClaimedStudentsMessage(result.claimedStudents.length),
      })
      onClaimedStudents?.(result.claimedStudents.map((student) => student.userId))
    } else {
      toast.info('Claim pending', {
        description: 'Your school needs to confirm the household match.',
      })
    }
    onClaimed?.()
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Use a school code"
      description="Loads the students your school already added under your invitation."
      submitLabel="Claim"
      submitDisabled={activationCode.trim().length === 0}
      onSubmit={handleSubmit}
      onReset={() => setActivationCode('')}
      size="md"
      leftFooter={
        onBack ? (
          <Button type="button" variant="ghost" onClick={onBack}>
            Back
          </Button>
        ) : undefined
      }>
      <FormField
        label="Activation code"
        htmlFor="claim-activation-code"
        helperText="Find this in the invitation email or letter from your school.">
        <Input
          id="claim-activation-code"
          value={activationCode}
          onChange={(event) => setActivationCode(event.target.value.toUpperCase())}
          placeholder="ABCD-1234"
          autoComplete="one-time-code"
        />
      </FormField>
    </FormSheet>
  )
}
